import { FC } from 'react';
import { isEmpty } from 'lodash';

import { ResourceType } from '@dbt-labs/dbt-dag';
import { TrustSignalsBadgeContainer } from '@dbt-labs/metadata-shared';
import { Link, LoadingBlock } from '@dbt-labs/sourdough';

import { SearchFieldType } from '@/__generated__/graphql';
import { TabType } from '@/components/DetailTabs';
import { ResourceChip } from '@/components/ResourceChip';
import { useLinkToExplorer } from '@/hooks/links';
import { useUrlProjectParams } from '@/hooks/params';
import { useGetLinkMapping } from '@/hooks/useGetLinkMapping';
import { parseUniqueId } from '@/typings/parseUniqueId';
import { UrlEnvironmentType } from '@/typings/url';
import {
  AccountParams,
  LinkGenerator,
  OriginColumnParams,
  OriginProjectParams,
  TabParams,
  UniqueIdParams,
  UrlProjectParams,
} from '@/util/linkTypes';
import { targetResourceSelector } from '@/util/selectorGenerators';
import { BOLD_TAG_REGEX, BoldedText, BoldSearchHighlight } from './BoldedText';
import { getTrustSignalsFromHit, SearchResultDisplayData } from './SearchResultsList';

type SkeletonItemParams = {
  data?: never;
  query?: never;
  testId?: string;
  fqn?: never;
  skeleton: true;
};

type SearchResultItemParams = {
  data: SearchResultDisplayData;
  query: string;
  testId?: string;
  skeleton?: never;
};

type SearchResultItemParamsWithProjectScope = SearchResultItemParams & {
  accountId: number;
  projectId: number;
  environmentType: UrlEnvironmentType;
  dbtUniqueId: string;
  getResourceLink: LinkGenerator<
    AccountParams &
      UrlProjectParams &
      UniqueIdParams &
      Partial<TabParams<TabType>> &
      Partial<OriginColumnParams> &
      Partial<OriginProjectParams>
  >;
};

const possiblyTruncate = (matchedField: SearchFieldType, text: string) => {
  switch (matchedField) {
    case SearchFieldType.Code:
    case SearchFieldType.Description:
      return `...${text}...`;
    case SearchFieldType.Column:
      const truncatedColumns = text.split(', ').slice(0, 5).join(', ').toUpperCase();
      if (truncatedColumns.length < text.length) {
        return `${truncatedColumns}...`;
      }
      return truncatedColumns;
    default:
      return text;
  }
};

const SecondLine = ({
  data,
  query,
  getResourceLink,
  ...projectScope
}: SearchResultItemParamsWithProjectScope) => {
  if (!data) return null;
  if (!data.highlight) return null;
  const truncatedText = possiblyTruncate(data.matchedField, data.highlight);
  const isColumnMatch = data.matchedField === 'column';
  const highlightedColumns = truncatedText.split(', ');
  return (
    <>
      Includes {data.matchedField}:{' '}
      {isColumnMatch &&
        highlightedColumns.map((column, index) => (
          <>
            {index > 0 && ', '}
            <Link
              key={index}
              to={getResourceLink({
                ...projectScope,
                tab: 'columns',
                originColumn: column.replace(BOLD_TAG_REGEX, ''),
              })}
              isInternal
            >
              <BoldSearchHighlight text={column} query={query} />
            </Link>
          </>
        ))}
      {!isColumnMatch && <BoldSearchHighlight text={truncatedText} query={query} />}
    </>
  );
};

const SKELETON_HEIGHT_PX = 60.67;

export const SearchResultItem: FC<SearchResultItemParams | SkeletonItemParams> = ({
  data: rawData,
  query,
  testId,
  skeleton,
}) => {
  const projectScope = useUrlProjectParams();
  const renderLineageLink = rawData?.hit?.fqn !== undefined;
  const getResourceLink = useLinkToExplorer('resourceDetails');
  const getLineageLink = useLinkToExplorer('projectLineage');
  const getLinkMapping = useGetLinkMapping();

  if (skeleton) {
    return (
      <li
        key="skeleton"
        aria-label="Loading..."
        data-testid={`skeleton-${testId}`}
        className="list-none overflow-hidden rounded-lg border border-borderMuted text-sm"
      >
        <LoadingBlock width={-1} height={SKELETON_HEIGHT_PX} />
      </li>
    );
  }

  if (!rawData || rawData?.hit?.name == null) {
    return null;
  }
  const hit = rawData.hit;
  const displayText =
    rawData.hit.resourceType === 'model' || rawData.hit.resourceType === 'source'
      ? parseUniqueId(rawData.hit.uniqueId).remainder // this includes the model version, which name does not
      : rawData.hit?.name || '';
  const shouldShowSecondLine =
    hit && rawData.matchedField !== 'name' && rawData.highlight;

  const trustSignals = getTrustSignalsFromHit(hit);
  const mappedLinks = getLinkMapping({
    healthIssues: trustSignals.healthIssues || [],
    projectId: projectScope.projectId,
    environmentType: projectScope.environmentType,
    uniqueId: hit.uniqueId,
  });
  if (!isEmpty(mappedLinks)) {
    trustSignals.additionalMetadata = {
      ...trustSignals.additionalMetadata,
      linkMapping: mappedLinks,
    };
  }

  return (
    <li
      key={hit.name || ''}
      aria-label={hit.name || ''}
      data-testid={testId}
      className="list-none overflow-hidden rounded-lg border border-borderMuted text-sm"
    >
      {skeleton && <LoadingBlock width={-1} height={SKELETON_HEIGHT_PX} />}
      {!skeleton && (
        <div className="bg-bgMain p-4">
          <div className="flex w-full items-center gap-2 overflow-hidden">
            <div className="flex-1 items-center overflow-hidden truncate">
              <span className="flex truncate">
                <Link
                  className="pr-2 text-base"
                  isInternal
                  to={getResourceLink({
                    ...projectScope,
                    dbtUniqueId: hit.uniqueId,
                  })}
                >
                  <BoldedText
                    text={displayText}
                    shouldBeBold={query}
                    boldProps={{ className: 'bg-bgBrandMuted' }}
                  />
                </Link>
                <TrustSignalsBadgeContainer
                  resourceType={hit.resourceType as ResourceType}
                  trustSignals={trustSignals}
                />
              </span>
            </div>
            {renderLineageLink && !!hit.fqn && (
              <span className="flex items-center text-sm">
                <Link
                  className="text-base"
                  isInternal
                  hideUnderline
                  to={getLineageLink({
                    ...projectScope,
                    selector: targetResourceSelector({
                      fqn: hit.fqn,
                      depth: 3,
                    }),
                    dbtUniqueId: hit.uniqueId,
                  })}
                >
                  View lineage
                </Link>
              </span>
            )}
            <span className="flex-0 text-sm">
              <ResourceChip resourceType={hit.resourceType} />
            </span>
          </div>
          {shouldShowSecondLine && (
            <div className="overflow-hidden truncate text-fgDecorative">
              <SecondLine
                {...projectScope}
                data={rawData}
                query={query}
                getResourceLink={getResourceLink}
                dbtUniqueId={rawData.hit.uniqueId}
              />
            </div>
          )}
        </div>
      )}
    </li>
  );
};
